import Layout from '@src/components/layout/Layout';

import {
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  styled,
  Typography,
} from '@mui/material';

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

import theme from '@src/theme';
import { useSnackbar } from 'notistack';

import HospitalsForm, {
  HospitalFormValues,
} from '../hospital-add/component/HospitalsForm';
import { RegionLookUps } from '@src/constants/Regions';
import useUpdateHospitalLazy from '@src/adapters/hooks/hospital/useUpdateHospital';
import { useGetHospitalLazy } from '@src/adapters/hooks/hospital/useGetHospitalLazy';
import { useNavigationStore } from '@src/adapters/stores/useNavigationStore';

const PREFIX = 'HospitalEdit';
const FORM_ID = 'hospital-edit-form';

const classes = {
  pageTitle: `${PREFIX}-pageTitle`,
  field: `${PREFIX}-field`,
};

const StyledLayout = styled(Layout)(({ theme }) => ({
  [`& .${classes.pageTitle}`]: {
    marginBottom: '33px',
  },
  [`& .${classes.field}`]: {
    width: '438px',

    '& .MuiInputBase-root': {
      height: '56px',
    },

    '& .MuiInputBase-input': {
      color: theme.palette.text.secondary,
    },
  },
}));

function HospitalEdit() {
  const { currentHospital } = useNavigationStore();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { execute: getHospital } = useGetHospitalLazy();
  const { execute: updateHospital } = useUpdateHospitalLazy();
  const [regionCode, setRegionCode] = useState<string>('');
  const {
    reset,
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm<HospitalFormValues>();

  useEffect(() => {
    (async () => {
      try {
        const hospital = await getHospital({ id: currentHospital?.id! });
        reset({
          shortCode: hospital.shortCode,
          name: hospital.name,
          codeSet: hospital.codeSet,
          regionCode: hospital.regionCode,
        });

        if (hospital.regionCode) {
          setRegionCode(hospital.regionCode);
        }
      } catch (error) {
        enqueueSnackbar('Failed to load hospital', { variant: 'error' });
      }
    })();
  }, [currentHospital?.id, enqueueSnackbar, getHospital, reset]);

  const onSubmit = async (data: any) => {
    if (!isDirty) {
      navigate(-1);
      return;
    }

    const result = await updateHospital({
      id: currentHospital?.id ?? '',
      name: data.name,
      codeSet: data.codeSet,
      regionCode: data.regionCode,
    });
    if (!result) {
      enqueueSnackbar('Failed to update hospital', { variant: 'error' });
      return;
    }
    enqueueSnackbar('Organisation Updated', { variant: 'success' });
    navigate(-2);
  };

  return (
    <StyledLayout>
      <Box sx={{ margin: 'auto', width: 949 }}>
        <Stack
          className={classes.pageTitle}
          direction={'row'}
          justifyContent="space-between">
          <Typography variant="h4" color={theme.palette.text.secondary}>
            Edit hospital
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}>
            <Button
              onClick={() => navigate(-1)}
              variant="outlined"
              size="large"
              color="primary">
              Cancel
            </Button>
            <Button
              type="submit"
              form="hospital-edit-form"
              variant="contained"
              size="large"
              color="primary">
              Finish
            </Button>
          </Box>
        </Stack>

        <Paper>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" color={theme.palette.text.secondary}>
              Hospital details
            </Typography>
          </Box>
          <Divider />
          <HospitalsForm
            formId={FORM_ID}
            register={register}
            onSubmit={handleSubmit(onSubmit)}
            fieldClassName={classes.field}
            regions={RegionLookUps}
            regionCode={regionCode}
            setRegionCode={setRegionCode}
            mode="edit"
          />
        </Paper>
      </Box>
    </StyledLayout>
  );
}

export default HospitalEdit;
